import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaBuilding } from "react-icons/fa";
import type { U2000Organizations } from "../context/auth/U2000AuthContext";

interface Props {
  organizations: U2000Organizations[] | null | undefined;
  onSelect: (org: U2000Organizations) => void;
}

const U2000OrganizationList: React.FC<Props> = ({ organizations, onSelect }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>("");

  const list = useMemo(() => {
    if (!organizations) return [] as U2000Organizations[];
    const f = filter.trim().toLowerCase();
    if (!f) return organizations;
    return organizations.filter((o) => (o.organizationName || "").toLowerCase().includes(f));
  }, [organizations, filter]);

  if (!organizations || organizations.length === 0) {
    return <div className="text-sm text-gray-500">{t("lblNoOrganizationsAvailable")}</div>;
  }

  return (
    <div className="w-full">
      <div className="mb-4">
        <input
          aria-label={t("lblFilterOrganization")}
          placeholder={t("lblFilterOrganization")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="w-full h-[46px] px-4 py-3 text-sm border border-gray-300 rounded-lg 
                     bg-white transition-all duration-200 focus:outline-hidden focus:ring-2 
                     focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-400
                     placeholder:text-gray-400"
        />
      </div>

      <div className="w-full h-[40vh] max-h-[40vh] overflow-auto pr-2">
        <ul role="listbox" aria-label={t("lblSelectOrganization")} className="grid w-full grid-cols-1 gap-3">
          {list.map((org) => (
            <li key={org.uuid} className="flex">
              <button
                type="button"
                role="option"
                tabIndex={0}
                onClick={() => onSelect(org)}
                className="flex h-[60px] w-full items-center justify-start rounded-lg px-4 py-3 
                           text-left transition-all duration-200 focus:outline-hidden focus:ring-2 
                           focus:ring-blue-500/20 border border-gray-300 hover:border-blue-400 
                           hover:bg-blue-50 hover:shadow-xs group"
              >
                <span className="flex w-full items-center gap-3">
                  <div
                    className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg 
                                  bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600 
                                  transition-colors duration-200"
                  >
                    <FaBuilding className="w-4 h-4" />
                  </div>
                  <span
                    className="flex-1 truncate text-left text-base font-medium text-gray-900 
                                   group-hover:text-blue-900 transition-colors duration-200"
                  >
                    {org.organizationName}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default U2000OrganizationList;
